import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../services/supabase.ts';
import { formatCurrency } from '../../utils/formatters.ts';
import { BillStatus } from '../../types/payment';
import '../../styles/Settlement.css';

interface RecordSettlementModalProps {
	onClose: () => void;
	onSubmit: (settlementData: any) => Promise<void>;
}

interface IBill {
	id: string;
	amount: number;
	status: BillStatus;
	paid_amount?: number;
	created_at: string;
}

const RecordSettlementModal: React.FC<RecordSettlementModalProps> = ({ onClose, onSubmit }) => {
	const [loading, setLoading] = useState(false);
	const [availableTenants, setAvailableTenants] = useState<any[]>([]);
	const [selectedTenantId, setSelectedTenantId] = useState('');
	const [totalRentDue, setTotalRentDue] = useState(0);
	const [totalPaid, setTotalPaid] = useState(0);
	const [additionalPayment, setAdditionalPayment] = useState(0);
	const [notes, setNotes] = useState('');
	const [selectedTenant, setSelectedTenant] = useState<any>(null);
	const [previousSettlements, setPreviousSettlements] = useState<any[]>([]);
	const [pendingBills, setPendingBills] = useState<IBill[]>([]);
	const [, setSettlementLoss] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		fetchEligibleTenants();
	}, []);

	const fetchAmounts = useCallback(async () => {
		try {
			const { data: bills } = await supabase
				.from('bills')
				.select('*')
				.eq('tenant_id', selectedTenantId)
				.in('status', ['Unpaid', 'Partial'])
				.order('created_at', { ascending: true });

			setPendingBills(bills || []);

			// Calculate total bills based on pending amounts
			const totalBills = (bills || []).reduce((sum, bill) => {
				const pendingAmount = bill.status === 'Partial' 
					? bill.amount - (bill.paid_amount || 0)
					: bill.amount;
				return sum + pendingAmount;
			}, 0);

			const { data: settlements } = await supabase
				.from('tenant_settlements')
				.select('*')
				.eq('tenant_id', selectedTenantId)
				.order('created_at', { ascending: false });

			const lastSettlement = settlements?.[0];
			if (lastSettlement) {
				setPreviousSettlements(settlements);
				setTotalRentDue(lastSettlement.outstanding_amount);
				setTotalPaid(0);
			} else {
				setTotalRentDue(totalBills);
				setTotalPaid(0); // No need to calculate payments since we're using pending amounts
			}
		} catch (error) {
			console.error('Error fetching amounts:', error);
		}
	}, [selectedTenantId]);

	const fetchTenantDetails = useCallback(async () => {
		const { data } = await supabase
			.from('tenants')
			.select('*, property:properties(*)')
			.eq('id', selectedTenantId)
			.single();

		if (data) {
			setSelectedTenant(data);
		}
	}, [selectedTenantId]);

	useEffect(() => {
		if (selectedTenantId) {
			fetchTenantDetails();
			fetchAmounts();
		}
	}, [selectedTenantId, fetchTenantDetails, fetchAmounts]);

	useEffect(() => {
		const handleEsc = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};
		window.addEventListener('keydown', handleEsc);
		return () => window.removeEventListener('keydown', handleEsc);
	}, [onClose]);

	const fetchEligibleTenants = async () => {
		try {
			const { data: tenants } = await supabase
				.from('tenants')
				.select(`
                    id,
                    full_name,
                    property_id,
                    security_deposit,
                    status,
                    bills:bills(id, amount, status),
                    tenant_settlements:tenant_settlements(id, outstanding_amount)
                `)
				.eq('status', 'Active');

			if (!tenants) return;

			const eligibleTenants = tenants.filter((tenant) => {
				const pendingBills = tenant.bills.filter(
					(bill) => bill.status !== 'Paid' && bill.status !== 'Cancelled'
				);
				return pendingBills.length > 0;
			});

			setAvailableTenants(eligibleTenants);
		} catch (error) {
			console.error('Error fetching eligible tenants:', error);
		}
	};

	const currentBalance = totalRentDue - totalPaid;
	const finalOutstanding = Math.max(0, currentBalance - additionalPayment);

	const handleAdditionalPaymentChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const value = e.target.value;
		if (value === '' || /^\d*$/.test(value)) {
			const numValue = parseInt(value) || 0;
			setAdditionalPayment(numValue);
		}
	};

	const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const text = e.target.value;
		if (text.length <= 50) {
			setNotes(text);
		}
	};

	const processSettlement = async (totalSettlementAmount: number) => {
		let remainingAmount = totalSettlementAmount;
		let totalLoss = 0;

		// Calculate loss from pending bills
		for (const bill of pendingBills) {
			const pendingAmount = bill.status === 'Partial' 
				? bill.amount - (bill.paid_amount || 0)
				: bill.amount;

			if (remainingAmount >= pendingAmount) {
				remainingAmount -= pendingAmount;
			} else {
				totalLoss += pendingAmount - remainingAmount;
				remainingAmount = 0;
			}
		}

		setSettlementLoss(totalLoss);

		// Mark all pending bills as paid
		const { error: billsError } = await supabase
			.from('bills')
			.update({
				status: 'Paid' as BillStatus,
				paid_amount: (b: any) => b.amount,
				payment_date: new Date().toISOString().split('T')[0],
			})
			.in('id', pendingBills.map((b) => b.id));

		if (billsError) throw billsError;

		return totalLoss;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!selectedTenant || isSubmitting) return;

		setLoading(true);
		setIsSubmitting(true);
		try {
			const totalSettlementAmount = additionalPayment;
			const lossAmount = await processSettlement(totalSettlementAmount);
			const settlementDate = new Date().toISOString().split('T')[0];

			// Record additional payment if any
			if (additionalPayment > 0) {
				const { error: paymentError } = await supabase
					.from('payments')
					.insert([{
						tenant_id: selectedTenant.id,
						property_id: selectedTenant.property_id,
						total_amount: additionalPayment,
						payment_date: settlementDate,
						payment_mode: 'Cash',
						payment_to: 'Other',
						notes: `Settlement payment - ${notes}\nLoss Amount: ${formatCurrency(lossAmount)}`,
						bill_id: null,
						settlement_payment: true
					}]);

				if (paymentError) throw paymentError;
			}

			// Create settlement record
			const { error: settlementError } = await supabase
				.from('tenant_settlements')
				.insert([{
					tenant_id: selectedTenant.id,
					property_id: selectedTenant.property_id,
					total_amount: totalRentDue,
					paid_amount: additionalPayment,
					outstanding_amount: finalOutstanding,
					security_deposit: selectedTenant.security_deposit,
					additional_payment: additionalPayment,
					settlement_date: settlementDate,
					settlement_notes: `${notes}\nTotal Loss: ${formatCurrency(lossAmount)}`,
					status: 'Cleared'  // Always set to Cleared
				}]);

			if (settlementError) throw settlementError;

			// Update tenant and property status
			const { error: tenantError } = await supabase
				.from('tenants')
				.update({ 
					status: 'Inactive', 
					deboarding_date: settlementDate 
				})
				.eq('id', selectedTenant.id);

			if (tenantError) throw tenantError;

			const { error: propertyError } = await supabase
				.from('properties')
				.update({ status: 'Vacant' })
				.eq('id', selectedTenant.property_id);

			if (propertyError) throw propertyError;

			await onSubmit({
				tenant_id: selectedTenant.id,
				property_id: selectedTenant.property_id,
				total_amount: totalRentDue,
				paid_amount: additionalPayment,
				outstanding_amount: finalOutstanding,
				settlement_date: settlementDate,
				settlement_notes: `${notes}\nTotal Loss: ${formatCurrency(lossAmount)}`,
				status: 'Cleared'  // Always set to Cleared
			});
			onClose();
		} catch (error: any) {
			console.error('Error in settlement:', error);
			alert(error.message || 'Failed to process settlement');
		} finally {
			setLoading(false);
			setIsSubmitting(false);
		}
	};

	const resetForm = () => {
		setTotalRentDue(0);
		setTotalPaid(0);
		setAdditionalPayment(0);
		setNotes('');
		setSelectedTenant(null);
		setPreviousSettlements([]);
	};

	return (
		<div className="modal">
			<div className="settlement-modal">
				<div className="settlement-header">
					<h2>Record Settlement</h2>
					<button className="close-button" onClick={onClose}>
						&times;
					</button>
				</div>

				<div className="tenant-selection">
					<select
						value={selectedTenantId}
						onChange={(e) => {
							const newTenantId = e.target.value;
							if (!newTenantId) {
								resetForm();
							}
							setSelectedTenantId(newTenantId);
						}}
						required
					>
						<option value="">Choose a tenant</option>
						{availableTenants.map((tenant) => (
							<option key={tenant.id} value={tenant.id}>
								{tenant.full_name}
							</option>
						))}
					</select>
				</div>

				{selectedTenant && (
					<div className="settlement-content">
						<div className="settlement-main">
							<form>
								{previousSettlements.length > 0 && (
									<div className="detail-row info">
										<p className="info-text">
											 💡 Previous settlement:{' '}
											{formatCurrency(
												previousSettlements[0]
													.outstanding_amount
											)}{' '}
											outstanding from{' '}
											{new Date(
												previousSettlements[0]
													.settlement_date
											).toLocaleDateString()}
										</p>
									</div>
								)}

								<div className="bill-summary">
									<div className="bill-summary-header">
										<span>Total Amount Due</span>
										<span className="amount-due">
											{formatCurrency(totalRentDue)}
										</span>
									</div>
								</div>

								<div className="detail-row">
									<label>Security Deposit</label>
									<span className="value">
										{formatCurrency(
											selectedTenant?.security_deposit ||
												0
										)}
									</span>
								</div>

								<div className="detail-row">
									<label>Additional Payment</label>
									<input
										type="number"
										className="amount-input"
										value={additionalPayment}
										onChange={handleAdditionalPaymentChange}
										min="0"
										step="any"
									/>
								</div>

								<div className="detail-row">
									<label>Final Outstanding</label>
									<span className="value amount-outstanding">
										{formatCurrency(finalOutstanding)}
									</span>
								</div>

								<div className="detail-row">
									<label>Notes</label>
									<textarea
										value={notes}
										onChange={handleNotesChange}
										rows={3}
										placeholder="Add settlement notes..."
									/>
								</div>

								{finalOutstanding > 0 && (
									<div className="detail-row warning">
										<p className="warning-text">
											 ⚠️ Outstanding:{' '}
											{formatCurrency(finalOutstanding)}
										</p>
									</div>
								)}
							</form>
						</div>

						<div className="settlement-sidebar">
							<div className="bill-summary-header">
								<h3>Pending Bills</h3>
								<span className="bill-count">
									{pendingBills.length} bills
								</span>
							</div>

							<div className="bills-list">
								{pendingBills.map((bill) => (
									<div key={bill.id} className="bill-item">
										<div className="bill-date">
											{new Date(
												bill.created_at
											).toLocaleDateString()}
										</div>
										<div className="bill-details">
											<span className="bill-amount">
												{formatCurrency(bill.amount)}
											</span>

											<span className="bill-pending">
												{formatCurrency(
													bill.amount -
														(bill.paid_amount || 0)
												)}
											</span>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				)}

				<div className="form-actions">
					<button
						type="button"
						className="btn-secondary"
						onClick={onClose}
					>
						Cancel
					</button>
					<button
						type="submit"
						className="btn-primary"
						disabled={loading || !selectedTenant || isSubmitting}
						onClick={handleSubmit}
					>
						{loading ? 'Processing...' : 'Confirm Settlement'}
					</button>
				</div>
			</div>
		</div>
	);
};

export default RecordSettlementModal;
